/**
 * Sidebar model (boards Admin-Dashboard, AF-Vue, FS-Vue, TD-Console, AG-Accueil, AG-Lecteur, AC-Cockpit).
 * One tree per SPACE; the switcher pill (« Création · Compta · Facturation · Technique », ADMIN only — a role
 * with a single space gets no switcher) picks the tree. Items are filtered by the permission matrix, never by role
 * name (except `roles` for persona homes such as « À faire aujourd'hui »).
 *
 * Owned by the foundation. An area that needs a new item adds it here in ONE small edit and says so in its report.
 */
import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  ArrowLeftRight,
  BookOpenText,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Cpu,
  FileText,
  FolderOpen,
  Inbox,
  Landmark,
  LayoutDashboard,
  MessagesSquare,
  PhoneCall,
  Receipt,
  Rocket,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";
import type { InboxSummary } from "@/contracts/shell";
import { roleCan, type Capability, type StaffRole } from "@/contracts/permissions";

export type Space = "creation" | "compta" | "facturation" | "technique" | "cabinet";

export const SPACE_LABEL: Record<Space, string> = {
  creation: "Création",
  compta: "Compta",
  facturation: "Facturation",
  technique: "Technique",
  cabinet: "Cabinet",
};

export interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  /** Per-role destination (LECTEUR's « Création d'entreprise » is the read-only list). */
  toFor?: Partial<Record<StaffRole, string>>;
  cap: Capability;
  /** Only these roles see it (persona homes). */
  roles?: StaffRole[];
  badge?: keyof InboxSummary;
  title?: string;
  children?: NavItem[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const finance: NavItem = {
  key: "finance",
  label: "Finance",
  icon: Wallet,
  to: "/finance",
  cap: "finance",
  title: "Finance : vue, paiements, factures émises, remboursements, abonnements, facturation cabinets, grilles",
  children: [
    { key: "finance.vue", label: "Vue finance", icon: Wallet, to: "/finance", cap: "finance" },
    { key: "finance.paiements", label: "Paiements", icon: Wallet, to: "/finance/paiements", cap: "finance", badge: "financePayments" },
    { key: "finance.factures", label: "Factures émises", icon: Wallet, to: "/finance/factures", cap: "finance" },
    { key: "finance.remboursements", label: "Remboursements", icon: Wallet, to: "/finance/remboursements", cap: "finance", badge: "financeRefunds" },
    { key: "finance.rapprochement", label: "Rapprochement", icon: Wallet, to: "/finance/rapprochement", cap: "finance" },
    { key: "finance.abonnements", label: "Abonnements", icon: Wallet, to: "/finance/abonnements", cap: "finance.admin" },
    { key: "finance.compta-mensuelle", label: "Compta mensuelle", icon: Wallet, to: "/finance/compta-mensuelle", cap: "finance.admin" },
    { key: "finance.cabinets", label: "Facturation cabinets", icon: Wallet, to: "/finance/facturation-cabinets", cap: "finance" },
    { key: "finance.grilles", label: "Grilles tarifaires", icon: Wallet, to: "/finance/grilles", cap: "finance.admin" },
  ],
};

const saas: NavItem = {
  key: "saas",
  label: "Facturation SaaS",
  icon: Receipt,
  to: "/facturation",
  cap: "saas",
  children: [
    { key: "saas.vue", label: "Vue d'ensemble", icon: Receipt, to: "/facturation", cap: "saas" },
    { key: "saas.comptes", label: "Comptes facturation", icon: Receipt, to: "/facturation/comptes", cap: "saas" },
    { key: "saas.flux", label: "Flux facture électronique", icon: Receipt, to: "/facturation/flux", cap: "saas", badge: "saasFlows" },
    { key: "saas.incidents", label: "Incidents et support", icon: Receipt, to: "/facturation/incidents", cap: "saas", badge: "saasIncidents" },
    { key: "saas.parametres", label: "Paramètres du produit", icon: Receipt, to: "/facturation/parametres", cap: "saas" },
    { key: "saas.plateforme", label: "Lancement des produits", icon: Rocket, to: "/plateforme", cap: "platform" },
  ],
};

const tech: NavItem = {
  key: "tech",
  label: "Technique",
  icon: Cpu,
  to: "/technique",
  cap: "tech.console",
  children: [
    { key: "tech.console", label: "Console CTO · debug", icon: Cpu, to: "/technique", cap: "tech.console" },
    { key: "tech.developpeurs", label: "Développeurs Melrys", icon: Cpu, to: "/technique/developpeurs", cap: "tech.developers" },
    { key: "tech.rssi", label: "RSSI Melrys", icon: ShieldCheck, to: "/technique/rssi", cap: "tech.security" },
  ],
};

const settings: NavItem = { key: "parametres", label: "Paramètres", icon: Settings, to: "/parametres", cap: "settings.personal" };
const reference: NavItem = { key: "reference", label: "Références", icon: BookOpenText, to: "/reference/roles", cap: "reference" };
const rdv: NavItem = { key: "rdv", label: "Rendez-vous", icon: CalendarDays, to: "/rendez-vous", cap: "bookings", badge: "meetLinksToSet" };

const administration = (...first: NavItem[]): NavSection => ({ label: "Administration", items: [...first, finance, settings, reference] });

export const NAV: Record<Space, NavSection[]> = {
  creation: [
    {
      label: "Dossiers",
      items: [
        { key: "creation.todo", label: "À faire aujourd'hui", icon: ClipboardCheck, to: "/creation/a-faire", cap: "creation.followup", roles: ["AGENT"] },
        { key: "creation.dossiers", label: "Création d'entreprise", icon: Building2, to: "/creation/nouvelles", toFor: { LECTEUR: "/creation/lecture", AGENT: "/creation/demandes" }, cap: "creation", badge: "creationNew" },
        { key: "creation.rappels", label: "Rappels président", icon: PhoneCall, to: "/creation/rappels", cap: "creation.followup" },
      ],
    },
    {
      label: "Suivi",
      items: [{ key: "creation.discussions", label: "Discussions", icon: MessagesSquare, to: "/creation/discussions", cap: "discussions", badge: "discussionsUnread" }, rdv],
    },
    administration(),
  ],
  compta: [
    { label: "Pilotage", items: [{ key: "compta.dashboard", label: "Tableau de bord", icon: LayoutDashboard, to: "/compta", cap: "cabinet.dashboard" }] },
    { label: "Dossiers", items: [{ key: "compta.clients", label: "Clients", icon: Users, to: "/compta/clients", cap: "cabinet.clients", badge: "onboardingsToDo" }] },
    {
      label: "Comptabilité",
      items: [
        { key: "compta.transactions", label: "Transactions", icon: ArrowLeftRight, to: "/compta/transactions", cap: "cabinet.operations" },
        { key: "compta.ecritures", label: "Écritures", icon: BookOpen, to: "/compta/ecritures", cap: "cabinet.operations" },
        { key: "compta.impots", label: "Impôts et taxes", icon: Landmark, to: "/compta/impots", cap: "cabinet.operations", badge: "taxesToDo" },
        { key: "compta.documents", label: "Documents", icon: FolderOpen, to: "/compta/documents", cap: "cabinet.operations" },
        // Admins: one inbox (accounting threads + creation chat + site chat). Accountants keep « Discussions » (cabinet).
        { key: "compta.messagerie", label: "Messagerie", icon: Inbox, to: "/messagerie", cap: "discussions", badge: "discussionsUnread" },
        { key: "compta.social", label: "Social", icon: Users, to: "/compta/social", cap: "cabinet.operations" },
        rdv,
      ],
    },
    administration(),
  ],
  facturation: [administration(saas)],
  technique: [{ label: "Administration", items: [tech, saas, finance, settings, reference] }],
  // COMPTABLE (AC-Cockpit): the accounting view of the companies attached to them; no switcher, no Finance.
  cabinet: [
    { label: "Pilotage", items: [{ key: "cabinet.cockpit", label: "Tableau de bord", icon: LayoutDashboard, to: "/cabinet", cap: "cabinet.dashboard" }] },
    {
      label: "Dossiers",
      items: [
        { key: "compta.clients", label: "Clients", icon: Users, to: "/compta/clients", cap: "cabinet.clients", badge: "onboardingsToDo" },
        { key: "creation.dossiers", label: "Création d'entreprise", icon: Building2, to: "/creation/lecture", cap: "creation" },
      ],
    },
    {
      label: "Comptabilité",
      items: [
        { key: "compta.transactions", label: "Transactions", icon: ArrowLeftRight, to: "/compta/transactions", cap: "cabinet.operations" },
        { key: "cabinet.revue", label: "Revue comptable", icon: ClipboardList, to: "/cabinet/revue", cap: "cabinet.operations" },
        { key: "compta.ecritures", label: "Écritures", icon: BookOpen, to: "/compta/ecritures", cap: "cabinet.operations" },
        { key: "compta.impots", label: "Impôts et taxes", icon: Landmark, to: "/compta/impots", cap: "cabinet.operations", badge: "taxesToDo" },
        { key: "compta.documents", label: "Documents", icon: FileText, to: "/compta/documents", cap: "cabinet.operations" },
        { key: "compta.discussions", label: "Discussions", icon: MessagesSquare, to: "/compta/discussions", cap: "discussions", badge: "discussionsUnread" },
        { key: "compta.social", label: "Social", icon: Users, to: "/compta/social", cap: "cabinet.operations" },
        rdv,
      ],
    },
    { label: "Cabinet", items: [settings, reference] },
  ],
};

/** Any sidebar item key ("finance.paiements"…). Checked at runtime against NAV (boards/registry.test.ts). */
export type NavKey = string;

/** Spaces a role can open, in switcher order. */
export function spacesFor(role: StaffRole): Space[] {
  if (role === "COMPTABLE") return ["cabinet"];
  const opens: [Space, Capability[]][] = [
    ["creation", ["creation"]],
    ["compta", ["cabinet.dashboard"]],
    ["facturation", ["saas"]],
    ["technique", ["tech.console", "tech.developers", "tech.security"]],
  ];
  return opens.filter(([, caps]) => caps.some((c) => roleCan(role, c))).map(([s]) => s);
}

/** Where a role lands after sign-in (and on « / »). */
export function homeFor(role: StaffRole): string {
  switch (role) {
    case "ADMIN":
      return "/compta";
    case "AGENT":
      return "/creation/a-faire";
    case "LECTEUR":
      return "/creation/lecture";
    case "COMPTABLE":
      return "/cabinet";
    case "RSSI":
      return "/technique/rssi";
    default:
      return "/technique";
  }
}

/** The tree filtered for a role: items it can read, sections left empty dropped. */
export function navFor(space: Space, role: StaffRole): NavSection[] {
  const keep = (i: NavItem): NavItem | null => {
    if (i.roles && !i.roles.includes(role)) return null;
    if (i.children) {
      // A group shows when one of its children does, and opens on the first one the role may see.
      const children = i.children.map(keep).filter((c): c is NavItem => !!c);
      return children.length ? { ...i, to: children[0]!.to, children } : null;
    }
    if (!roleCan(role, i.cap)) return null;
    return { ...i, to: i.toFor?.[role] ?? i.to };
  };
  return NAV[space].map((s) => ({ ...s, items: s.items.map(keep).filter((x): x is NavItem => !!x) })).filter((s) => s.items.length > 0);
}

export function allNavKeys(): Set<string> {
  const out = new Set<string>();
  const walk = (i: NavItem) => {
    out.add(i.key);
    i.children?.forEach(walk);
  };
  Object.values(NAV).forEach((secs) => secs.forEach((s) => s.items.forEach(walk)));
  return out;
}
